import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import LessonContent from "../components/LessonContent";
import Quiz from "../components/Quiz";
import Checklist from "../components/Checklist";
import PaymentModal from "../components/PaymentModal";
import Support from "../components/Support";
import { curriculum } from "../data/curriculum";
import { useAuth } from "../context/AuthContext";
import { SITE } from "../data/siteData";

const FREE_LESSONS = ["w1l1"];

function findLesson(id) {
  for (const section of curriculum) {
    const found = section.lessons.find(l => l.id === id);
    if (found) return { section, lesson: found };
  }
  return null;
}

function allLessons() {
  return curriculum.flatMap(s => s.lessons);
}

function isLastLessonOfSection(lessonId) {
  for (const section of curriculum) {
    const last = section.lessons[section.lessons.length - 1];
    if (last && last.id === lessonId) return true;
  }
  return false;
}

export default function CoursePage() {
  const { currentUser, hasFullAccess } = useAuth();
  const navigate = useNavigate();
  const first = curriculum[0]?.lessons[0]?.id || "";
  const [currentId, setCurrentId] = useState(first);
  const [showPayment, setShowPayment] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (!currentUser) {
    navigate("/auth");
    return null;
  }

  const found = findLesson(currentId);
  if (!found) return null;
  const { section, lesson } = found;

  const isFree = FREE_LESSONS.includes(lesson.id);
  const canView = isFree || hasFullAccess;
  const isLastLesson = isLastLessonOfSection(lesson.id);

  const allLessonsList = allLessons();
  const currentIndex = allLessonsList.findIndex(l => l.id === currentId);
  const prevLesson = currentIndex > 0 ? allLessonsList[currentIndex - 1] : null;
  const nextLesson = currentIndex < allLessonsList.length - 1
    ? allLessonsList[currentIndex + 1]
    : null;

  const handleSelect = (id) => {
    setCurrentId(id);
    setMobileNavOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <div style={{ display: "flex", height: "calc(100vh - 46px)", overflow: "hidden" }}>

      <div className="sl-sidebar-desktop" style={{ display: "flex", flexShrink: 0 }}>
        <Sidebar currentLessonId={currentId} onSelectLesson={handleSelect} />
      </div>

      {mobileNavOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 400 }}
          onClick={() => setMobileNavOpen(false)}
        >
          <div
            style={{ width: 260, height: "100%", background: "#fff", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}
          >
            <Sidebar currentLessonId={currentId} onSelectLesson={handleSelect} />
          </div>
        </div>
      )}

      <button
        onClick={() => setMobileNavOpen(!mobileNavOpen)}
        className="sl-sidebar-toggle"
        style={{
          position: "fixed", bottom: 16, left: 16, zIndex: 500,
          display: "none", background: "#009dc4", border: "none",
          borderRadius: "50%", width: 44, height: 44,
          color: "#fff", fontSize: 18, cursor: "pointer",
        }}
      >
        ☰
      </button>

      <div style={{
        flex: 1, overflowY: "auto", padding: "20px 28px",
        maxWidth: 820, fontFamily: "'Nunito', 'Segoe UI', Arial, sans-serif",
      }}>

        {!canView ? (
          <div style={{ maxWidth: 520, paddingTop: 8 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              background: "#f5f5f5", border: "1px solid #ddd",
              borderRadius: 3, padding: "4px 12px", marginBottom: 18,
            }}>
              <span style={{ fontSize: 13 }}>🔒</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#666" }}>
                {section.title} — Full course required
              </span>
            </div>

            <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1a1a1a", marginBottom: 8 }}>
              {lesson.title}
            </h2>
            <p style={{ color: "#555", fontSize: 14, lineHeight: 1.8, marginBottom: 24 }}>
              This lesson is part of the full course. Unlock everything with a one-time payment
              of <strong>{SITE.priceFull}</strong> via M-Pesa. No subscription. No expiry.
            </p>

            <button
              onClick={() => setShowPayment(true)}
              style={{
                display: "block", width: "100%", padding: "11px 0",
                background: "#009dc4", border: "none", borderRadius: 4,
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: "pointer", marginBottom: 12, textAlign: "center",
                fontFamily: "inherit",
              }}
            >
              Pay {SITE.priceFull} via M-Pesa →
            </button>

            <div style={{
              background: "#f9f9f9", border: "1px solid #e0e0e0",
              borderLeft: "4px solid #009dc4", borderRadius: 4,
              padding: "14px 16px", marginBottom: 16,
            }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: "#282a35", margin: "0 0 6px" }}>
                Already paid and received your access code?
              </p>
              <p style={{ fontSize: 12, color: "#666", margin: "0 0 10px", lineHeight: 1.6 }}>
                Enter your code to unlock the course immediately.
              </p>
              <button
                onClick={() => navigate("/unlock")}
                style={{
                  padding: "8px 20px", background: "#fff",
                  border: "1px solid #009dc4", borderRadius: 4,
                  color: "#009dc4", fontSize: 13, fontWeight: 700,
                  cursor: "pointer", width: "100%", fontFamily: "inherit",
                }}
              >
                Enter my access code →
              </button>
            </div>

            <Support />
          </div>

        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: "#009dc4",
                textTransform: "uppercase", letterSpacing: 1,
              }}>
                {section.title}
                {isFree && (
                  <span style={{
                    marginLeft: 8, background: "#dcfce7", color: "#16a34a",
                    fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 3,
                  }}>
                    FREE PREVIEW
                  </span>
                )}
              </span>
              <h1 style={{
                fontSize: 22, fontWeight: 800, color: "#1a1a1a",
                margin: "4px 0 4px", lineHeight: 1.3,
              }}>
                {lesson.title}
              </h1>
              <p style={{ color: "#666", fontSize: 14, margin: 0 }}>
                {lesson.subtitle}
              </p>
            </div>

            <hr style={{ border: "none", borderTop: "2px solid #009dc4", marginBottom: 20 }} />

            <LessonContent blocks={lesson.content} />

            {isLastLesson && lesson.quiz && (
              <Quiz quiz={lesson.quiz} lessonId={lesson.id} />
            )}
            {isLastLesson && lesson.checklist && (
              <Checklist items={lesson.checklist} lessonId={lesson.id} />
            )}

            {isFree && !hasFullAccess && (
              <div style={{
                marginTop: 28, background: "#282a35", borderRadius: 4,
                padding: "22px 20px", borderLeft: "4px solid #009dc4",
              }}>
                <h3 style={{ color: "#fff", fontWeight: 800, fontSize: 17, margin: "0 0 8px" }}>
                  Ready to continue?
                </h3>
                <p style={{ color: "#aaa", fontSize: 13, margin: "0 0 16px", lineHeight: 1.7 }}>
                  The full course covers every major freelance skill in depth. One payment, lifetime access.
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => setShowPayment(true)}
                    style={{
                      padding: "9px 22px", background: "#009dc4",
                      border: "none", borderRadius: 4, color: "#fff",
                      fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    Pay {SITE.priceFull} →
                  </button>
                  <button
                    onClick={() => navigate("/unlock")}
                    style={{
                      padding: "9px 22px", background: "none",
                      border: "1px solid #555", borderRadius: 4,
                      color: "#ccc", fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    Enter access code
                  </button>
                </div>
              </div>
            )}

            <div style={{
              display: "flex", justifyContent: "space-between",
              marginTop: 28, paddingTop: 16, borderTop: "1px solid #e0e0e0", gap: 12,
            }}>
              <button
                onClick={() => { if (prevLesson) { setCurrentId(prevLesson.id); window.scrollTo(0,0); } }}
                disabled={!prevLesson}
                style={{
                  padding: "8px 18px",
                  background: prevLesson ? "#f5f5f5" : "transparent",
                  border: "1px solid #e0e0e0", borderRadius: 4,
                  color: prevLesson ? "#333" : "#ccc", fontSize: 13, fontWeight: 600,
                  cursor: prevLesson ? "pointer" : "default", fontFamily: "inherit",
                  maxWidth: "48%", textAlign: "left",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
              >
                {prevLesson ? `← ${prevLesson.title}` : "← Start"}
              </button>
              <button
                onClick={() => { if (nextLesson) handleSelect(nextLesson.id); }}
                disabled={!nextLesson}
                style={{
                  padding: "8px 18px",
                  background: nextLesson ? "#009dc4" : "transparent",
                  border: nextLesson ? "none" : "1px solid #e0e0e0",
                  borderRadius: 4, color: nextLesson ? "#fff" : "#ccc",
                  fontSize: 13, fontWeight: 700,
                  cursor: nextLesson ? "pointer" : "default", fontFamily: "inherit",
                  maxWidth: "48%", textAlign: "right",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
              >
                {nextLesson ? `${nextLesson.title} →` : "Complete"}
              </button>
            </div>
          </>
        )}
      </div>

      {showPayment && (
        <PaymentModal onClose={() => setShowPayment(false)} currentUser={currentUser} />
      )}

      <style>{`
        @media (min-width: 768px) {
          .sl-sidebar-desktop { display: flex !important; }
          .sl-sidebar-toggle { display: none !important; }
        }
        @media (max-width: 767px) {
          .sl-sidebar-desktop { display: none !important; }
          .sl-sidebar-toggle { display: flex !important; }
        }
      `}</style>
    </div>
  );
}